import * as React from 'react'

import { Box, Button, Section, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { UserSelectorInput } from 'modules/core/components/Inputs/UserSelector'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import bookkeepers from 'modules/bookkeepers/store/collections/bookkeepers'
import payrollCollection from 'modules/payroll_bundle/store/collections/payroll'

const ProfessionalSettingsContent = () => {
  const { loading } = useAsyncData(async () => {
    await Promise.all([payrollCollection.fetch(), bookkeepers.fetch()])

    const bookkeepersIds = bookkeepers
      .filter((bookkeeper) => {
        const userId = bookkeeper.attributes.access_id

        const user = users.get(userId)

        if (!user) {
          return false
        }

        return true
      })
      .map((bookkeeper) => bookkeeper.attributes.access_id)

    setBookkeeperIds(bookkeepersIds)
  }, [])

  const [bookkeepersModal, setBookkeepersModal] = React.useState(false)
  const [bookkeeperIds, setBookkeeperIds] = React.useState<number[]>([])
  const [isSubmitting, setSubmitting] = React.useState(false)
  const { company } = useCurrentSession()

  const updateBookkeepers = (newBookkeepersIds: number[]) => {
    setSubmitting(true)
    bookkeepers
      .rpc('bulk_update', {
        bookkeepers_ids: newBookkeepersIds,
        company_id: company.id,
      })
      .then((models) => {
        bookkeepers.reset(models)
        setBookkeepersModal(false)
        setSubmitting(false)
        setBookkeeperIds(newBookkeepersIds)
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      })
  }

  const removeInactiveBookkeeper = (userId: number) => {
    const activeBookkeepers = bookkeeperIds.filter((id) => id !== userId)
    updateBookkeepers(activeBookkeepers)
  }

  const submitLabel = bookkeeperIds.length
    ? i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.submit')
    : i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.submit_nobookkeeper')

  const bookkeepersList = bookkeepers.map((bookkeeper) => {
    const userId = bookkeeper.attributes.access_id
    const user = users.get(userId)

    if (!user) {
      return null
    }

    const userIsInactive = !user.isActive
    const isLast = bookkeepers.last() === bookkeeper

    return (
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        border={
          !isLast && {
            bottom: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }
        }
        padding="s16"
        key={bookkeeper.attributes.id}
      >
        <Text.Normal size="300" color="grey1000" weight="normal">
          {user.fullName()}
        </Text.Normal>

        {userIsInactive && (
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box>
              <Tag size="small" leftItem="circle" showCircle type={Tag.Types.grey}>
                {i18n.t(
                  'app_store_v2.apps.payroll_bundle.settings.professional_settings.status.inactive'
                )}
              </Tag>
            </Box>

            <Button
              type="secondary"
              onClick={() => removeInactiveBookkeeper(userId)}
              iconName={'delete'}
            ></Button>
          </Box>
        )}
      </Box>
    )
  })

  const noBookkeepers = (
    <Text.Normal size="300" color="grey800" weight="medium">
      {i18n.t('app_store_v2.apps.payroll_bundle.settings.no_bookkeeper_selected')}
    </Text.Normal>
  )

  if (loading) return <Loading />

  return (
    <Box gap="s24">
      <Box
        borderRadius={{
          all: 'abs016',
        }}
        border={
          bookkeepers.length
            ? {
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }
            : {}
        }
      >
        <Box>{bookkeepers.length ? bookkeepersList : noBookkeepers}</Box>
        {bookkeepersModal && (
          <Modal
            illustration="user-tie"
            title={i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.title')}
            description={i18n.t(
              'app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.modal_description'
            )}
            onClose={() => setBookkeepersModal(false)}
            onSubmit={() => {
              updateBookkeepers(bookkeeperIds)
            }}
            footer={
              <>
                <Button type="secondary" onClick={() => setBookkeepersModal(false)}>
                  {i18n.t('common.cancel')}
                </Button>
                <Button disabled={isSubmitting} submit>
                  {submitLabel}
                </Button>
              </>
            }
          >
            <Box gap="s24">
              <UserSelectorInput
                label={i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.label')}
                onSubmit={setBookkeeperIds}
                selectedIds={bookkeeperIds}
                users={users.active}
                multiselect
                allowsEmpty
              />
            </Box>
          </Modal>
        )}
      </Box>

      <Button
        type="secondary"
        onClick={() => setBookkeepersModal(true)}
        iconName={bookkeepers.length ? 'users' : 'add'}
      >
        {bookkeepers.length
          ? i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.button.manage')
          : i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.button.add')}
      </Button>
    </Box>
  )
}

const ProfessionalSettings = () => (
  <Section
    title={i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.section_title')}
    illustration="briefcase"
    description={i18n.t(
      'app_store_v2.apps.payroll_bundle.settings.professional_settings.section_description'
    )}
  >
    <ProfessionalSettingsContent />
  </Section>
)

export default observer(ProfessionalSettings)
